import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen, Clapperboard, Disc, Loader2, Music, Play, Search, Tag, Tv, User, X } from "lucide-react";
import { Cover } from "@/components/Cover";
import { VideoThumb } from "@/components/VideoBits";
import { usePlayer } from "@/context/PlayerContext";
import { api, getLibMode, setLibMode } from "@/lib/api";
import { SEARCH_HINT, openSearch } from "@/components/GlobalSearch";

/* Which library the top-bar search covers, from the current route. */
export const scopeFor = (pathname) => (/^\/videos/.test(pathname) ? "videos" : /^\/audiobooks/.test(pathname) ? "books" : "music");
const SCOPE = {
  music: { label: "Music", placeholder: "Songs, albums, artists, genres…", icon: Music },
  videos: { label: "Videos", placeholder: "Titles, actors, genres…", icon: Clapperboard },
  books: { label: "Audiobooks", placeholder: "Titles, authors, genres…", icon: BookOpen },
};
const HEAD = { artists: "Artists", albums: "Albums", tracks: "Songs", genres: "Genres", shows: "Shows", videos: "Movies & episodes", authors: "Authors", books: "Books" };
const cache = new Map();
if (typeof window !== "undefined") window.addEventListener("auth:logout", () => cache.clear());

/* Top-bar search: a real input whose results drop down under it (no page overlay), fuzzy and scoped to the page you're on. */
export const TopSearch = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const p = usePlayer();
  const scope = scopeFor(pathname);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const input = useRef(null);
  const Icon = SCOPE[scope].icon;

  useEffect(() => { setQ(""); setGroups([]); setOpen(false); }, [scope]);
  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, []);
  useEffect(() => {
    const needle = q.trim();
    if (!needle) { setGroups([]); setLoading(false); return undefined; }
    const key = `${scope}|${needle.toLowerCase()}`;
    if (cache.has(key)) { setGroups(cache.get(key)); setActive(0); return undefined; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search/scoped", { params: { q: needle, scope } })
        .then((r) => { cache.set(key, r.data.groups); if (cache.size > 200) cache.delete(cache.keys().next().value); setGroups(r.data.groups); setActive(0); })
        .catch(() => setGroups([]))
        .finally(() => setLoading(false));
    }, 140);
    return () => clearTimeout(t);
  }, [q, scope]);

  const flat = useMemo(() => groups.flatMap((g) => g.items.map((it) => ({ kind: g.kind, it }))), [groups]);
  const close = () => { setOpen(false); input.current?.blur(); };
  const go = (path, lib) => {
    if (lib === "owner" && getLibMode() !== "owner") setLibMode("owner");
    else if (!lib && getLibMode() === "owner") setLibMode("mine");
    close(); navigate(path);
  };
  const pick = ({ kind, it }) => {
    if (kind === "tracks") { p.playTracks([it]); close(); return; }
    if (kind === "albums" || kind === "books") return go(kind === "books" ? `/audiobooks/${it.id}` : `/album/${it.id}`, it.lib);
    if (kind === "artists") return go(`/artist?name=${encodeURIComponent(it.name)}`, it.lib);
    if (kind === "authors") return go(`/audiobooks?q=${encodeURIComponent(it.name)}`);
    if (kind === "genres") return go(`/albums?genre=${encodeURIComponent(it.name)}&sort=name`);
    if (kind === "shows") return go(`/videos/group/${encodeURIComponent(`show:${it.name}`)}`);
    if (kind === "videos") return go(`/videos/watch/${it.id}`);
  };
  const onKey = (e) => {
    if (e.key === "Escape") { if (q) { setQ(""); setGroups([]); } else close(); return; }
    if (!flat.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % flat.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + flat.length) % flat.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(flat[active]); }
  };
  useEffect(() => { box.current?.querySelector(`[data-idx="${active}"]`)?.scrollIntoView({ block: "nearest" }); }, [active]);

  const show = open && q.trim().length > 0;
  const [mobTop, setMobTop] = useState(56);
  useEffect(() => { if (show && box.current) setMobTop(Math.round(box.current.getBoundingClientRect().bottom) + 6); }, [show]);
  let idx = -1;
  return (
    <div ref={box} className="relative ml-auto w-full min-w-0 max-w-md" data-testid="top-search" data-scope={scope}>
      <div className={`flex h-9 items-center gap-2 rounded-full border bg-white pl-3 pr-2 text-xs transition-[border-color,box-shadow] ${show ? "border-zinc-900 shadow-lg shadow-zinc-900/10" : "border-zinc-200 hover:border-zinc-400"}`}>
        <Search size={13} className="shrink-0 text-zinc-400" />
        <span className="hidden shrink-0 items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-600 sm:flex" data-testid="top-search-scope"><Icon size={10} />{SCOPE[scope].label}</span>
        <input ref={input} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKey}
          placeholder={SCOPE[scope].placeholder} aria-label={`Search ${SCOPE[scope].label}`} className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-400" data-testid="top-search-input" />
        {loading ? <Loader2 size={13} className="shrink-0 animate-spin text-zinc-400" /> : q ? (
          <button onClick={() => { setQ(""); setGroups([]); input.current?.focus(); }} className="shrink-0 rounded-full p-1 text-zinc-400 hover:text-zinc-900" aria-label="Clear" data-testid="top-search-clear"><X size={12} /></button>
        ) : (
          <button onClick={openSearch} className="hidden shrink-0 rounded border border-zinc-200 bg-zinc-50 px-1 font-mono text-[10px] text-zinc-500 hover:border-zinc-900 hover:text-zinc-900 sm:inline" title={`Everything search (${SEARCH_HINT})`} data-testid="nav-search-button">{SEARCH_HINT}</button>
        )}
      </div>
      {show && (
        <div style={{ "--mt": `${mobTop}px` }} className="fixed inset-x-2 top-[var(--mt)] z-50 max-h-[70vh] sm:absolute sm:inset-x-0 sm:top-11 overflow-y-auto rounded-xl border border-zinc-200 bg-white/95 p-1.5 shadow-2xl shadow-zinc-900/15 backdrop-blur-md animate-in fade-in slide-in-from-top-1 duration-150" role="listbox" data-testid="top-search-results">
          {flat.length === 0 && !loading && <p className="px-3 py-6 text-center text-xs text-zinc-500" data-testid="top-search-empty">Nothing in your {SCOPE[scope].label.toLowerCase()} matches “{q.trim()}”. <button onClick={openSearch} className="underline">Search everything</button></p>}
          {groups.map((g) => (
            <div key={g.kind} className="mb-1" data-testid={`top-search-group-${g.kind}`}>
              <p className="px-2 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">{HEAD[g.kind]}</p>
              {g.items.map((it) => {
                idx += 1;
                const i = idx;
                return <Row key={`${g.kind}-${it.id || it.name}`} kind={g.kind} it={it} active={i === active} idx={i} onHover={() => setActive(i)} onPick={() => pick({ kind: g.kind, it })} />;
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Row = ({ kind, it, active, idx, onHover, onPick }) => {
  const Thumb = () => {
    if (kind === "albums" || kind === "books") return <Cover albumId={it.id} hasCover={it.has_cover} version={it.cover_version} className="h-9 w-9 shrink-0" rounded="rounded" iconSize={12} />;
    if (kind === "tracks") return <Cover albumId={it.album_id} className="h-9 w-9 shrink-0" rounded="rounded" iconSize={12} />;
    if (kind === "videos") return <VideoThumb id={it.id} hasThumb={it.has_thumb} thumbV={it.thumb_v} className="h-9 w-16 shrink-0 rounded" />;
    const I = kind === "shows" ? Tv : kind === "genres" ? Tag : kind === "artists" || kind === "authors" ? User : Disc;
    return <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-500"><I size={14} /></span>;
  };
  const title = it.title || it.name;
  const sub = kind === "tracks" ? `${it.artist} · ${it.album}` : kind === "albums" ? `${it.artist}${it.year ? ` · ${it.year}` : ""} · ${it.song_count} songs`
    : kind === "artists" ? `${it.albums} album${it.albums === 1 ? "" : "s"}` : kind === "genres" ? `${it.albums} album${it.albums === 1 ? "" : "s"}`
      : kind === "shows" ? `${it.episodes} episode${it.episodes === 1 ? "" : "s"}${it.year ? ` · ${it.year}` : ""}` : kind === "videos" ? [it.label, it.via].filter(Boolean).join(" · ")
        : kind === "authors" ? `${it.books} book${it.books === 1 ? "" : "s"}` : `${it.artist}${it.year ? ` · ${it.year}` : ""}${it.genre ? ` · ${it.genre}` : ""}`;
  return (
    <button type="button" role="option" aria-selected={active} data-idx={idx} onMouseEnter={onHover} onClick={onPick} data-testid={`top-search-${kind}-${it.id || it.name}`}
      className={`flex w-full items-center gap-3 rounded-lg px-2 py-1.5 text-left transition-colors ${active ? "bg-zinc-900 text-white" : "hover:bg-zinc-100"}`}>
      <Thumb />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{title}</span>
        <span className={`block truncate text-[11px] ${active ? "text-zinc-300" : "text-zinc-500"}`}>{sub}</span>
      </span>
      {it.lib === "owner" && <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] uppercase ${active ? "bg-white/15" : "bg-sky-50 text-sky-700"}`}>Shared</span>}
      {kind === "tracks" && <Play size={12} className={active ? "text-white" : "text-zinc-400"} />}
    </button>
  );
};
